
import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
import { Toast, Dialog, Icon, Loading, Button } from 'vant';
import axios from 'axios';
import { getOssSignature } from '@/api/api';

Vue.use(Toast).use(Dialog).use(Icon).use(Loading).use(Button);

enum FileStatus {
  waiting = 50,
  uploading = 100,
  success = 200,
  fail = 500,
}

interface IPPTItem {
  url: string;
  name: string;
  size: number;
  status: FileStatus;
  file?: File;
  fileType?: 'ppt' | 'pptx';
}

@Component({
  components: {
    [Icon.name]: Icon,
    [Loading.name]: Loading,
    [Button.name]: Button,
  },
})
export default class PptUpload extends Vue {
  @Prop({ default: 5 }) maxCount!: number; // 最大上传数量
  @Prop({ default: 50 }) maxSize!: number; // 单个文件最大大小（MB）
  @Prop({ default: 0 }) minSize!: number; // 单个文件最小大小（MB）
  @Prop({ default: false }) required!: boolean; // 是否必传
  @Prop({ default: [] }) defaultFiles!: Array<{
    url: string;
    name: string;
    size: number;
  }>; // 默认文件列表
  @Prop({ type: String, default: '' }) customDir!: string; // 自定义OSS目录
  @Prop({ type: Function }) getOssSignatureFn!: any; // 自定义OSS签名函数
  @Prop({ default: 0 }) itemIdx!: number; // 默认下标
  // Data
  pptList: IPPTItem[] = [];
  showUploadBtn = true;
  readyToUploadQueue: IPPTItem[] = [];
  FileStatus = FileStatus;
  showPreview = false;
  previewUrl = '';
  imgCdnPrefix = process.env.VUE_APP_OSS_IMG;

  @Watch('defaultFiles', { immediate: true })
  onDefaultFilesChange(files: any[]) {
    if (files && files.length) {
      this.pptList = files.map((item) => ({
        ...item,
        status: FileStatus.success,
        fileType: this.getFileType(item.name),
      }));
      this.updateUploadBtnStatus();
    }
  }

  @Watch('pptList.length')
  onPptListChange() {
    this.updateUploadBtnStatus();
  }

  @Watch('readyToUploadQueue.length')
  onQueueChange() {
    this.updateUploadBtnStatus();
  }

  mounted() {
    this.updateUploadBtnStatus();
  }

  /**
   * 更新上传按钮显示状态
   */
  updateUploadBtnStatus() {
    const successCount = this.pptList.filter(
      (item) => item.status === FileStatus.success,
    ).length;
    const waitingCount = this.readyToUploadQueue.filter(
      (item) => item.status === FileStatus.waiting,
    ).length;
    const total = successCount + waitingCount;

    this.showUploadBtn = total < this.maxCount;
  }

  /**
   * 获取文件类型
   */
  getFileType(fileName: string): 'ppt' | 'pptx' {
    const parts = fileName.split('.');
    let ext = '';
    if (parts.length > 1) {
      ext = parts[parts.length - 1].toLowerCase();
    }
    if (ext === 'pptx') { return 'pptx'; }
    if (ext === 'ppt') { return 'ppt'; }
    return 'pptx';
  }

  /**
   * 处理文件选择
   */
  handleFileSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    const files = input.files;

    if (!files || !files.length) {
      return;
    }

    const file = files[0];

    // 验证文件类型
    const validTypes = [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ];
    const parts = file.name.split('.');
    const fileExt = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';

    if (!validTypes.includes(file.type) && !['ppt', 'pptx'].includes(fileExt)) {
      Toast.fail('请上传 PPT 或 PPTX 格式的文件');
      input.value = '';
      return;
    }

    // 验证文件大小
    const fileSizeMB = file.size / 1024 / 1024;
    if (fileSizeMB > this.maxSize) {
      Toast.fail(`文件大小不能超过 ${this.maxSize}MB`);
      input.value = '';
      return;
    }

    if (this.minSize > 0 && fileSizeMB < this.minSize) {
      Toast.fail(`文件大小不能小于 ${this.minSize}MB`);
      input.value = '';
      return;
    }

    // 验证数量
    if (this.pptList.length + this.readyToUploadQueue.length >= this.maxCount) {
      Toast.fail(`最多只能上传 ${this.maxCount} 个文件`);
      input.value = '';
      return;
    }

    // 添加到待上传队列
    const pptItem: IPPTItem = {
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      status: FileStatus.waiting,
      file,
      fileType: this.getFileType(file.name),
    };

    this.readyToUploadQueue.push(pptItem);
    this.pptList.push(pptItem);

    // 清空 input 值
    input.value = '';

    // 自动开始上传
    this.uploadToOss();
  }

  /**
   * 上传到 OSS
   */
  async uploadToOss() {
    const uploadingItems = this.readyToUploadQueue.filter(
      (item) => item.status === FileStatus.waiting,
    );

    if (!uploadingItems.length) {
      // 如果没有待上传的文件，更新按钮状态并返回
      this.updateUploadBtnStatus();
      return;
    }

    try {
      Toast.loading({
        message: '上传中...',
        forbidClick: true,
        duration: 0,
      });

      const ossSignature = await this.getOssSignature();

      const uploadPromises = uploadingItems.map((item, index) => {
        return this.uploadSingleFile(item, index, ossSignature);
      });

      const results = await Promise.all(uploadPromises);

      Toast.clear();
      const successCount = results.filter((r) => r).length;
      const failCount = results.filter((r) => !r).length;

      // 清理失败的待上传项（保留在列表中但标记为失败）
      // 如果用户想重试，可以保留失败的文件
      this.readyToUploadQueue = this.readyToUploadQueue.filter(
        (item) =>
          item.status === FileStatus.waiting || item.status === FileStatus.fail,
      );

      // 更新按钮状态
      this.updateUploadBtnStatus();

      if (successCount > 0 && failCount === 0) {
        Toast.success(`成功上传 ${successCount} 个文件`);
      } else if (successCount > 0 && failCount > 0) {
        Toast.fail(`成功 ${successCount} 个，失败 ${failCount} 个`);
      } else {
        Toast.fail('上传失败，请重试');
      }

      this.$emit('upload-success', this.getUploadedFiles());
    } catch (error) {
      Toast.clear();
      Toast.fail('上传失败，请重试');
      console.error('PPT上传失败:', error);
      this.$emit('upload-error', error);
    }
  }

  /**
   * 获取 OSS 签名
   */
  async getOssSignature() {
    if (this.getOssSignatureFn) {
      return await this.getOssSignatureFn();
    }
    const res: any = await getOssSignature();
    return res.data;
  }

  /**
   * 上传单个文件
   */
  async uploadSingleFile(
    item: IPPTItem,
    queueIndex: number,
    ossSignature: any,
  ): Promise<string | null> {
    const { policy, accessid, callback, signature, host, dir } = ossSignature;

    item.status = FileStatus.uploading;

    const formData = new FormData();
    const parts = item.name.split('.');
    const fileExt =
      parts.length > 1 ? parts[parts.length - 1].toLowerCase() : 'pptx';
    const fileName = this.generateRandomString(10) + '.' + fileExt;
    const key = (dir || this.customDir) + 'ppt/' + +new Date() + '_' + fileName;

    formData.append('key', key);
    formData.append('policy', policy);
    formData.append('OSSAccessKeyId', accessid);
    formData.append('success_action_status', '200');
    formData.append('callback', callback);
    formData.append('signature', signature);
    formData.append('name', item.name);
    formData.append('file', item.file!);

    try {
      const response = await axios({
        url: 'https://' + host,
        data: formData,
        method: 'post',
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });

      if (response.status === 200) {
        item.status = FileStatus.success;
        item.url = this.imgCdnPrefix + key;

        // 关键：从待上传队列中移除已成功的文件
        const queueIdx = this.readyToUploadQueue.indexOf(item);
        if (queueIdx > -1) {
          this.readyToUploadQueue.splice(queueIdx, 1);
        }

        // 更新按钮状态
        this.updateUploadBtnStatus();

        this.$emit('file-uploaded', {
          index: this.itemIdx,
          url: item.url,
          name: item.name,
          size: item.size,
          fileType: item.fileType,
        });
        return item.url;
      } else {
        throw new Error('上传失败');
      }
    } catch (error) {
      item.status = FileStatus.fail;
      console.error('上传失败:', error);
      this.$emit('file-upload-error', {
        name: item.name,
        error,
      });
      return null;
    }
  }

  /**
   * 删除 PPT
   */
  removePPT(index: number) {
    const item = this.pptList[index];

    // 如果文件正在上传，不允许删除
    if (item.status === FileStatus.uploading) {
      Toast.fail('文件正在上传，请等待完成');
      return;
    }

    this.readyToUploadQueue = this.readyToUploadQueue.filter(
      (queueItem) => queueItem !== item,
    );

    // 或者使用索引删除
    // const queueIndex = this.readyToUploadQueue.indexOf(item);
    // if (queueIndex > -1) {
    //   this.readyToUploadQueue.splice(queueIndex, 1);
    // }

    // 从列表中移除
    this.pptList.splice(index, 1);

    // 更新按钮状态
    this.updateUploadBtnStatus();

    this.$emit('file-removed', item);
  }
  /**
   * 获取 Office Online 预览 URL
   */
  getOfficePreviewUrl(fileUrl: string): string {
    // 使用 Microsoft Office Online 预览
    const encodedUrl = encodeURIComponent(fileUrl);
    return `https://view.officeapps.live.com/op/view.aspx?src=${encodedUrl}`;
  }

  /**
   * 预览 PPT
   */
  previewPPT(url: string) {
    if (!url) {
      Toast.fail('文件地址无效');
      return;
    }
    // 如果是 blob URL，先下载再预览
    if (url.startsWith('blob:')) {
      this.downloadPPT(url);
      return;
    }
    this.previewUrl = url;
    this.showPreview = true;
  }

  /**
   * 下载 PPT
   */
  downloadPPT(url: string) {
    if (!url) { return; }
    const link = document.createElement('a');
    link.href = url;
    link.download = url.split('/').pop() || 'presentation.pptx';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * 获取已上传的文件列表
   */
  getUploadedFiles(): Array<{
    url: string;
    name: string;
    size: number;
    fileType?: string;
  }> {
    return this.pptList
      .filter((item) => item.status === FileStatus.success)
      .map(({ url, name, size, fileType }) => ({ url, name, size, fileType }));
  }

  /**
   * 获取所有已上传的文件 URL
   */
  getFileUrls(): string[] {
    return this.pptList
      .filter((item) => item.status === FileStatus.success)
      .map((item) => item.url);
  }

  /**
   * 重置组件
   */
  reset() {
    this.pptList = [];
    this.readyToUploadQueue = [];
    this.showPreview = false;
    this.previewUrl = '';
    this.updateUploadBtnStatus();
    this.$emit('reset');
  }

  /**
   * 格式化文件大小
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) { return '0 B'; }
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * 生成随机字符串
   */
  generateRandomString(length = 10): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * 获取文件状态文本
   */
  getFileStatusText(status: FileStatus): string {
    const map = {
      [FileStatus.waiting]: '待上传',
      [FileStatus.uploading]: '上传中...',
      [FileStatus.success]: '已上传',
      [FileStatus.fail]: '上传失败',
    };
    return map[status] || '';
  }
}
